import { useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import { ArrowLeft, BookOpen, Check, CheckCheck, Inbox, MessagesSquare, PenLine, RefreshCcw } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import {
  TriageTicketFilters,
  useResolveAllTriage,
  useSetTriageStatus,
  useTriageCounts,
  useTriageSync,
  useTriageTickets,
} from '../api/useAdminTriage';
import { RefreshButton, timeAgo } from '../components/ops/opsUi';
import TriageTicketDetail from '../components/triage/TriageTicketDetail';
import {
  CategoryChip,
  DispositionChip,
  PriorityMark,
  TriageAvatar,
} from '../components/triage/triageUi';

// Feedback Triage inbox (docs/TRIAGE.md §7): ticket list left, ticket detail
// right; on small screens the list navigates to the detail. Selection lives
// in the URL (/triage/:ticketId) so tickets are deep-linkable.

type CountsData = NonNullable<AdminAPI.GetTriageCountsResponse['data']>;

interface FilterTab {
  key: string;
  label: string;
  filters: TriageTicketFilters;
  count?: (counts: CountsData) => number;
  /** Body for "Mark all done" on this tab; omitted on closed views (resolved, spam). */
  resolveAll?: AdminAPI.ResolveAllTriageTicketsRequest;
}

const FILTER_TABS: FilterTab[] = [
  { key: 'open', label: 'Open', filters: {}, count: (c) => c.open, resolveAll: {} },
  {
    key: 'needs_review',
    label: 'Needs review',
    filters: { disposition: 'needs_review' },
    count: (c) => c.needsReview,
    resolveAll: { disposition: 'needs_review' },
  },
  {
    key: 'needs_action',
    label: 'Needs action',
    filters: { disposition: 'needs_action' },
    count: (c) => c.needsAction,
    resolveAll: { disposition: 'needs_action' },
  },
  {
    key: 'auto_answerable',
    label: 'Auto-answerable',
    filters: { disposition: 'auto_answerable' },
    count: (c) => c.autoAnswerable,
    resolveAll: { disposition: 'auto_answerable' },
  },
  {
    key: 'unclassified',
    label: 'Unclassified',
    filters: { status: 'new' },
    count: (c) => c.unclassified,
    resolveAll: { status: 'new' },
  },
  {
    key: 'waiting_on_user',
    label: 'Waiting on user',
    filters: { status: 'waiting_on_user' },
    count: (c) => c.waitingOnUser,
    resolveAll: { status: 'waiting_on_user' },
  },
  { key: 'resolved', label: 'Resolved', filters: { status: 'resolved' } },
  { key: 'spam', label: 'Spam', filters: { status: 'spam' } },
];

function TicketRow({
  ticket,
  selected,
  onClick,
  onMarkDone,
}: {
  ticket: AdminAPI.TriageTicketSummary;
  selected: boolean;
  onClick: () => void;
  /** Omitted on closed tickets (resolved, spam). */
  onMarkDone?: () => void;
}) {
  const displayName = ticket.matchedUserName ?? ticket.counterpartName ?? ticket.counterpartEmail;
  return (
    <div
      className={`flex items-stretch border-b border-neutral-100 dark:border-neutral-800 transition-colors ${
        selected
          ? 'bg-primary-50 dark:bg-primary-900/20'
          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
      }`}
    >
      <button
        onClick={onClick}
        className={`min-w-0 flex-1 text-left pl-3 py-2.5 ${onMarkDone ? 'pr-1' : 'pr-3'}`}
      >
        <div className="flex items-center gap-2.5">
          <TriageAvatar name={displayName} imageUrl={ticket.matchedUserImageUrl} size={8} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium truncate flex-1">{displayName ?? 'Unknown'}</span>
              {ticket.hasActiveDraft && (
                <span
                  title="Reply draft awaiting review"
                  className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary-500"
                >
                  <PenLine className="w-3 h-3" />
                  draft
                </span>
              )}
              {ticket.threadCount > 1 && (
                <span
                  title={`${ticket.threadCount} threads`}
                  className="inline-flex items-center gap-0.5 text-[10px] text-dark-muted"
                >
                  <MessagesSquare className="w-3 h-3" />
                  {ticket.threadCount}
                </span>
              )}
              <span className="text-[11px] text-dark-muted whitespace-nowrap">
                {timeAgo(ticket.lastInboundAt ?? ticket.createdAt)}
              </span>
            </div>
            <p className="text-xs text-dark-muted truncate">
              {ticket.title ?? ticket.summary ?? '(no summary yet)'}
            </p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <CategoryChip category={ticket.category} />
              <DispositionChip disposition={ticket.disposition} />
              <PriorityMark priority={ticket.priority} />
            </div>
          </div>
        </div>
      </button>
      {onMarkDone && (
        <button
          onClick={onMarkDone}
          title="Mark done"
          aria-label="Mark done"
          className="shrink-0 px-2.5 text-dark-muted hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
        >
          <Check className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function AdminTriage() {
  const toast = useToast();
  const [, params] = useRoute('/triage/:ticketId');
  const [, navigate] = useLocation();
  const selectedId = params?.ticketId ?? null;

  const [activeTabKey, setActiveTabKey] = useState('open');
  const activeTab = FILTER_TABS.find((t) => t.key === activeTabKey) ?? FILTER_TABS[0];

  const { data: countsData } = useTriageCounts();
  const counts = countsData ?? null;

  const { data, isLoading, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTriageTickets(activeTab.filters);
  const tickets = data?.pages.flatMap((p) => p.data?.tickets ?? []) ?? [];

  // "Mark all done" resolves whatever the active tab shows. On Open that is the
  // open set (tickets waiting on a user reply are kept, as before); on the
  // Waiting-on-user tab it is exactly those. Closed tabs have no sweep.
  const { confirm } = useConfirm();
  const resolveAll = useResolveAllTriage();
  const tabCount = counts && activeTab.count ? activeTab.count(counts) : 0;
  const canMarkAllDone = !!activeTab.resolveAll && (tickets.length > 0 || tabCount > 0);
  const runMarkAllDone = async () => {
    if (!activeTab.resolveAll) return;
    const scope =
      activeTab.key === 'open'
        ? 'open tickets (unclassified, classified, and awaiting approval). Tickets waiting on a user reply are kept.'
        : activeTab.key === 'waiting_on_user'
          ? 'tickets waiting on a user reply.'
          : `open tickets in "${activeTab.label}". Tickets waiting on a user reply are kept.`;
    const ok = await confirm({
      title: `Mark all ${activeTab.label.toLowerCase()} tickets done?`,
      message: `This resolves ${tabCount > 0 ? `all ${tabCount}` : 'all'} ${scope} Pending drafts are discarded and nothing is sent.`,
      confirmText: 'Mark all done',
      type: 'danger',
    });
    if (!ok) return;
    resolveAll.mutate(activeTab.resolveAll, {
      onSuccess: (res) => toast.success(`Marked ${res.data?.resolved ?? 0} ticket(s) done`),
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Mark all done failed'),
    });
  };

  // Per-row "done": the list updates eagerly (see useSetTriageStatus), so only
  // failures need a toast.
  const setStatus = useSetTriageStatus();
  const markDone = (ticketId: string) => {
    setStatus.mutate(
      { ticketId, status: 'resolved' },
      { onError: (err) => toast.error(err instanceof Error ? err.message : 'Mark done failed') }
    );
  };

  const sync = useTriageSync();
  const runSync = () => {
    sync.mutate(undefined, {
      onSuccess: (res) => {
        const d = res.data;
        if (!d?.enabled) {
          toast.error(d?.skipped ?? d?.error ?? 'Gmail sync is not configured');
          return;
        }
        toast.success(
          `Synced: ${d.inbound ?? 0} inbound, ${d.outbound ?? 0} outbound, ${d.ticketsCreated ?? 0} new ticket(s)`
        );
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Sync failed'),
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Inbox className="w-5 h-5 text-primary-500" />
        <h1 className="text-xl font-bold">Feedback Triage</h1>
        <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
        <button
          onClick={runSync}
          disabled={sync.isPending}
          title="Pull new mail from the support mailbox now"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />
          {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
        <button
          onClick={runMarkAllDone}
          disabled={resolveAll.isPending || !canMarkAllDone}
          title={
            activeTab.resolveAll
              ? `Resolve every ticket in "${activeTab.label}" and discard pending drafts`
              : 'Switch to an open view to mark tickets done'
          }
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
        >
          <CheckCheck className="w-3.5 h-3.5" />
          {resolveAll.isPending ? 'Marking…' : 'Mark all done'}
        </button>
        <Link
          href="/triage/playbook"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Playbook
          {(counts?.pendingPlaybookProposals ?? 0) > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
              {counts?.pendingPlaybookProposals}
            </span>
          )}
        </Link>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {FILTER_TABS.map((tab) => {
          const badge = counts && tab.count ? tab.count(counts) : 0;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTabKey(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                activeTab.key === tab.key
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                  : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
              }`}
            >
              {tab.label}
              {badge > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-neutral-200 dark:bg-neutral-700 text-[10px] font-bold leading-none">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex gap-4 items-start">
        {/* Ticket list */}
        <div
          className={`w-full lg:w-96 lg:shrink-0 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden ${
            selectedId ? 'hidden lg:block' : ''
          }`}
        >
          {isLoading && <p className="text-sm text-dark-muted p-4">Loading…</p>}
          {!isLoading && tickets.length === 0 && (
            <p className="text-sm text-dark-muted p-4">No tickets here. 🎉</p>
          )}
          {tickets.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              selected={ticket.id === selectedId}
              onClick={() => navigate(`/triage/${ticket.id}`)}
              onMarkDone={
                ticket.status === 'resolved' || ticket.status === 'spam' ? undefined : () => markDone(ticket.id)
              }
            />
          ))}
          {hasNextPage && (
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="w-full py-2.5 text-sm text-primary-500 font-medium hover:underline disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>

        {/* Detail pane */}
        <div className={`flex-1 min-w-0 ${selectedId ? '' : 'hidden lg:block'}`}>
          {selectedId ? (
            <>
              <button
                onClick={() => navigate('/triage')}
                className="lg:hidden inline-flex items-center gap-1 mb-3 text-sm text-dark-muted hover:text-primary-500"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to inbox
              </button>
              <TriageTicketDetail ticketId={selectedId} />
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 p-10 text-center">
              <p className="text-sm text-dark-muted">Select a ticket to see the conversation.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
